import { createContext, useContext, useState } from "react";
import { apiGet, apiPost } from "../services/Api/apiCall";
import { Call, Device, TwilioError } from "@twilio/voice-sdk";
import { translate } from "../services/Translations/translatorService";
import { useCase } from "./CaseProvider";
import { Activity } from "../types/Activity";
import { CalendlyEvent } from "../types/Calendly/CalendlyEvent";
import { enqueueSnackbar } from "notistack";
import { getCalendlyEventReference } from "../services/calendlyService";

type CallEndCallback = () => void;

const getTwilioErrorMessage = (error: unknown) => {
  if (!(error instanceof Error)) {
    return "Unbekannter Fehler";
  }

  const code = "code" in error && typeof error.code === "number" ? ` (Twilio ${error.code})` : "";
  return `${error.message}${code}`;
};

type CallContextType = {
  status: string;
  phoneError: string;
  makeCall: (phoneNumber: string) => Promise<void>;
  hangUp: () => void;
  cancelRecording: () => Promise<void>;
  recordingEnabled: boolean;
  setRecordingEnabled: (enabled: boolean) => void;
  call?: Call | null;
  device?: Device | null;
  callModalOpen: boolean;
  openCallModal: (activity: Activity | null, callback?: CallEndCallback) => Promise<void>;
  closeCallModal: () => void;
  activity?: Activity | null;
  muted: boolean;
  mute: () => void;
  unmute: () => void;
  toggleMute: () => void;
};

const CallContext = createContext<CallContextType | null>(null);

export const CallProvider = ({ children }: { children: React.ReactNode }) => {
  const [device, setDevice] = useState<Device | null>(null);
  const [recordingEnabled, setRecordingEnabled] = useState<boolean>(false);
  const [callModalOpen, setCallModalOpen] = useState<boolean>(false);
  const [status, setStatus] = useState(translate("call.callStatus.initializing"));
  const [call, setCall] = useState<Call | null>(null);
  const [activity, setActivity] = useState<Activity | null>(null);
  const [calendlyEvent, setCalendlyEvent] = useState<CalendlyEvent | null>(null);
  const [phoneError, setPhoneError] = useState("");
  const [onCallEnd, setOnCallEnd] = useState<CallEndCallback | null>(null);
  const [muted, setMuted] = useState<boolean>(false);
  const { product } = useCase();

  const applyMute = (shouldMute: boolean) => {
    if (!call) {
      return;
    }

    if (call.status() !== "open") {
      call.once("accept", () => applyMute(shouldMute));
      return;
    }

    call.mute(shouldMute);

    const stream = (call as any).getLocalStream?.();
    if (stream) {
      stream.getAudioTracks().forEach((t: { enabled: boolean }) => (t.enabled = !shouldMute));
    }

    setMuted(call.isMuted());
  };

  const mute = () => applyMute(true);
  const unmute = () => applyMute(false);
  const toggleMute = () => {
    if (!call) {
      return;
    }
    applyMute(!call.isMuted());
  };

  const handleCallError = (error: unknown) => {
    setCall(null);
    setMuted(false);
    setPhoneError(getTwilioErrorMessage(error));
    setStatus(translate("call.callStatus.failed"));
  };

  const createTwilioDevice = async (): Promise<Device | null> => {
    if (device && device.state !== Device.State.Destroyed) {
      return device;
    }

    const token = await handleTokenFetch();

    if (token) {
      const newDevice = new Device(token, {
        logLevel: "error",
      });

      newDevice.on("error", (deviceError: TwilioError.TwilioError) => {
        setPhoneError(getTwilioErrorMessage(deviceError));
      });

      newDevice.on("tokenWillExpire", async () => {
        const refreshedToken = await handleTokenFetch();

        if (refreshedToken && newDevice.state !== Device.State.Destroyed) {
          newDevice.updateToken(refreshedToken);
        }
      });

      newDevice.on("incoming", (connection) => {
        connection.accept();
        setCall(connection);
        setStatus(translate("call.callStatus.inCallWithInboundCaller"));

        connection.on("accept", () => setMuted(connection.isMuted()));
        connection.on("mute", (isMuted: boolean) => setMuted(isMuted));
        setMuted(connection.isMuted());
      });

      newDevice.on("disconnect", () => {
        setCall(null);
        setMuted(false);
        setStatus(translate("call.callStatus.ended"));
        if (onCallEnd) {
          onCallEnd();
          setOnCallEnd(null);
        }
      });

      setDevice(newDevice);
      setStatus(translate("call.callStatus.ready"));
      return newDevice;
    }

    return null;
  };

  const openCallModal = async (activity: Activity | null, callback?: CallEndCallback) => {
    if (!call) {
      await createTwilioDevice();

      const calendlyEventReference = activity ? getCalendlyEventReference(activity.calendlyEvent) : null;
      if (activity && calendlyEventReference) {
        const calendlyEvent = await apiGet<CalendlyEvent>(calendlyEventReference);

        setCalendlyEvent(calendlyEvent);

        setActivity(activity);
      }

      if (callback) {
        setOnCallEnd(() => callback);
      }
    }

    setCallModalOpen(true);
  };

  const closeCallModal = () => {
    if (!call) {
      setActivity(null);
      setCalendlyEvent(null);
    }

    setCallModalOpen(false);
  };

  const handleTokenFetch = async () => {
    try {
      const response = await apiPost("/twilio/get_token", {});
      return response.token;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setPhoneError(`Failed to fetch token: ${err.message}`);
      } else {
        setPhoneError("Failed to fetch token: Unbekannter Fehler");
      }
    }

    return false;
  };

  const makeCall = async (phoneNumber: string) => {
    if (!product) {
      return;
    }

    const params: Device.ConnectOptions["params"] = {
      To: phoneNumber,
      caseId: product.id.toString(),
      lbProductClass: product.backofficeCase.lbProductClass,
      recordingEnabled: recordingEnabled.toString(),
    };

    if (calendlyEvent) {
      params["calendlyEventId"] = calendlyEvent.id.toString();

      setPhoneError("");
      const currentDevice = await createTwilioDevice();

      if (!currentDevice) {
        setStatus(translate("call.callStatus.failed"));
        return;
      }

      setStatus(`Wähle ${phoneNumber}...`);

      try {
        const activeCall = await currentDevice.connect({ params });
        let callFailed = false;

        activeCall.on("error", (callError) => {
          callFailed = true;
          handleCallError(callError);
        });

        activeCall.on("disconnect", () => {
          setCall(null);
          setMuted(false);
          if (!callFailed) {
            setStatus(translate("call.callStatus.ended"));
          }
          if (onCallEnd) {
            onCallEnd();
            setOnCallEnd(null);
          }
        });

        activeCall.on("mute", (isMuted: boolean) => setMuted(isMuted));
        setMuted(activeCall.isMuted());

        setCall(activeCall);
      } catch (error: unknown) {
        handleCallError(error);
      }
    } else {
      enqueueSnackbar(
        "Calendly ID nicht gefunden. Bitte Call Dialog erneut öffnen ggf. Fallseite neu laden. Wenn das Problem weiter besteht die IT informieren.",
        {
          variant: "error",
          isNonInteractive: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        }
      );
    }
  };

  const hangUp = () => {
    if (call) {
      call.disconnect();
    }
  };

  const cancelRecording = async () => {
    await apiPost("/twilio/cancel_recording", {
      calendlyEventId: calendlyEvent?.id,
    });

    setRecordingEnabled(false);
  };

  return (
    <CallContext.Provider
      value={{
        status,
        phoneError,
        makeCall,
        hangUp,
        cancelRecording,
        recordingEnabled,
        setRecordingEnabled,
        call,
        device,
        callModalOpen,
        openCallModal,
        closeCallModal,
        activity,
        muted,
        mute,
        unmute,
        toggleMute,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useOptionalCall = () => useContext(CallContext);

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error("useCall must be used within a CallProvider");
  }
  return context;
};
